import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Copy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { getMedia, uploadMedia } from "@/lib/api";

const KEYS = [
    { key: "profile", label: "Profile photo", hint: "Shown prominently on the Home hero and About page." },
    { key: "banner", label: "Home banner", hint: "Wide banner image at the very top of the Home page (behind the portrait). Use a landscape photo or an academic building image. Recommended: 2400×900 or larger." },
    { key: "og-image", label: "Open Graph image", hint: "Used when the site is shared on social platforms." },
    { key: "project-refugees", label: "Refugees project image", hint: "Featured on Projects." },
];

export default function AdminMedia() {
    const [media, setMedia] = useState({});
    const inputs = useRef({});

    const refresh = async () => {
        const out = {};
        for (const m of KEYS) {
            const r = await getMedia(m.key);
            out[m.key] = r?.url || null;
        }
        setMedia(out);
    };

    useEffect(() => { refresh(); }, []);

    const onUpload = async (key, file) => {
        if (!file) return;
        try {
            const r = await uploadMedia(key, file);
            setMedia((m) => ({ ...m, [key]: r.url }));
            toast.success("Uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Media</p>
                    <h1 className="mt-1 font-display text-3xl sm:text-4xl">Photos & assets</h1>
                </div>
                <Button variant="ghost" onClick={refresh} className="text-paper/80 hover:bg-white/[0.06]"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {KEYS.map((m) => (
                    <Card key={m.key} className="p-5 rounded-2xl bg-white/[0.04] border-white/10">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="font-display text-xl">{m.label}</p>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">key: {m.key}</p>
                                <p className="text-sm text-paper/70 mt-2">{m.hint}</p>
                            </div>
                            <div className="h-24 w-24 rounded-xl overflow-hidden bg-white/[0.06] border border-white/10 grid place-items-center">
                                {media[m.key] ? (
                                    <img src={media[m.key]} alt="" className="h-full w-full object-cover" />
                                ) : (
                                    <p className="text-[10px] text-paper/50">no image</p>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                            <input ref={(el) => { inputs.current[m.key] = el; }} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(m.key, e.target.files?.[0])} data-testid={`admin-media-upload-input-${m.key}`} />
                            <Button onClick={() => inputs.current[m.key]?.click()} className="bg-paper text-ink hover:bg-paper/90" data-testid={`admin-media-upload-button-${m.key}`}>
                                <Upload className="h-4 w-4 mr-2" /> Upload
                            </Button>
                            {media[m.key] && (
                                <Button variant="ghost" className="text-paper/80 hover:bg-white/[0.06]" onClick={() => { navigator.clipboard.writeText(media[m.key]); toast.success("Copied data URL"); }}>
                                    <Copy className="h-4 w-4 mr-2" /> Copy URL
                                </Button>
                            )}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
